/**
 * AdminCampaigns — all active fundraisers, filterable, with quick actions.
 *
 * Read-only view of every campaign (from the CAMPAIGNS mock). Actions:
 *   - Feature / unfeature (writes to homepage curation)
 *   - Pause / close
 *   - Flag for review
 *
 * TODO(backend): everything here is client-only. Wire GET /api/campaigns +
 *   the PATCH mutation endpoints.
 */

import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Rocket, Star, Flag, Search } from "lucide-react";
import { SectionHeader, StatusBadge, AdminButton } from "./_ui";
import { CAMPAIGN_ROSTER } from "../../data/adminMock";

const fmt$ = (n) => `$${Number(n).toLocaleString()}`;

const FILTERS = [
  { key: "all",     label: "All",     predicate: () => true },
  { key: "live",    label: "Live",    predicate: (c) => (c.daysLeft ?? 0) > 0 },
  { key: "closing", label: "Closing", predicate: (c) => c.daysLeft > 0 && c.daysLeft <= 7 },
  { key: "closed",  label: "Closed",  predicate: (c) => (c.daysLeft ?? 0) <= 0 },
];

export const AdminCampaigns = () => {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [featured, setFeatured] = useState(new Set([0, 1, 2]));

  const rows = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter).predicate;
    return CAMPAIGN_ROSTER.filter(f).filter((c) => {
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return c.name.toLowerCase().includes(q) || c.school.toLowerCase().includes(q);
    });
  }, [filter, query]);

  const toggleFeatured = (idx, name) => {
    setFeatured((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
        toast(`${name} removed from homepage`);
      } else {
        if (next.size >= 3) {
          toast.error("Only 3 campaigns can be featured. Remove one first.");
          return prev;
        }
        next.add(idx);
        toast.success(`${name} featured on homepage`);
      }
      return next;
    });
  };

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Campaigns"
        title="All active fundraisers."
        description="Feature a campaign on the landing page, close it early, or flag it for review."
      />

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            data-testid={`camp-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition ${
              filter === f.key
                ? "bg-ink text-sand"
                : "bg-sand text-ink ring-1 ring-inset ring-ink/15 hover:ring-ink/40"
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto inline-flex items-center gap-2 rounded-full bg-sand px-3 py-1.5 ring-1 ring-inset ring-ink/15">
          <Search className="h-3.5 w-3.5 text-ink/50" strokeWidth={1.75} />
          <input
            type="search"
            placeholder="Search by name or school"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-56 bg-transparent text-xs text-ink outline-none placeholder:text-ink/40"
            data-testid="campaigns-search"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-ink/10 bg-sand">
        <table className="min-w-full text-sm">
          <thead className="bg-beige/60 text-left text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/60">
            <tr>
              <th className="px-5 py-3">Student · school</th>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Progress</th>
              <th className="px-5 py-3">Days left</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10">
            {rows.map((c) => {
              const pct = Math.min(100, Math.round((c.raised / c.goal) * 100));
              const status = c.daysLeft <= 0 ? "closed" : c.daysLeft <= 7 ? "pending" : "live";
              const isFeat = featured.has(c.idx);
              return (
                <tr key={c.idx} className="hover:bg-ink/[0.03]">
                  <td className="px-5 py-4">
                    <p className="font-medium text-ink">{c.name}</p>
                    <p className="mt-0.5 text-xs text-ink/60">{c.school}</p>
                  </td>
                  <td className="px-5 py-4 text-ink/80">{c.category}</td>
                  <td className="px-5 py-4">
                    <p className="text-ink">{fmt$(c.raised)} <span className="text-ink/50">of {fmt$(c.goal)}</span></p>
                    <div className="mt-1.5 h-1.5 w-40 rounded-full bg-ink/10">
                      <div className="h-full rounded-full bg-yellow" style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td className="px-5 py-4 text-ink/80">{c.daysLeft > 0 ? `${c.daysLeft} d` : "—"}</td>
                  <td className="px-5 py-4"><StatusBadge status={status} /></td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        data-testid={`btn-feature-${c.idx}`}
                        onClick={() => toggleFeatured(c.idx, c.name)}
                        title={isFeat ? "Remove from homepage" : "Feature on homepage"}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full ring-1 ring-inset transition ${
                          isFeat
                            ? "bg-yellow text-ink ring-yellow"
                            : "bg-sand text-ink/50 ring-ink/15 hover:text-ink hover:ring-ink/40"
                        }`}
                      >
                        <Star className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        data-testid={`btn-flag-${c.idx}`}
                        onClick={() => toast(`${c.name} flagged for review`, { description: "Ops team notified." })}
                        title="Flag for review"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sand text-ink/50 ring-1 ring-inset ring-ink/15 transition hover:text-scarlet hover:ring-scarlet/40"
                      >
                        <Flag className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </button>
                      <AdminButton
                        size="sm"
                        variant="secondary"
                        onClick={() => toast(`Opening ${c.name}'s campaign…`)}
                        testid={`btn-open-${c.idx}`}
                      >
                        <Rocket className="h-3 w-3" strokeWidth={1.75} /> Open
                      </AdminButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
